// Client for the RingbaX admin chat endpoint on the MCP worker.
//
// POST <worker>/admin-chat -> text/event-stream
// Body: { prompt, accountId, threadHistory? }
//
// Auth: the caller passes a Cognito access token; we send it as
// `Authorization: Bearer <jwt>`. On 401 we invoke `refreshAccessToken`
// (if supplied) and retry once. This module never imports Vuex — the
// store wires the token + refresh callback in. Keeps the network layer
// unit-testable in isolation.
//
// The worker emits these SSE events, which we translate to the panel's
// internal shape on the fly:
//
//   { type: "text_chunk",  text, phase: "plan" | "answer" }
//   { type: "tool_start",  id, name, args }
//   { type: "tool_result", id, result }
//   { type: "citations",   items: [{ label, to }] }
//   { type: "done" }
//   { type: "error",       message }

const DEFAULT_WORKER_URL = process.env.VUE_APP_RINGBA_AI_WORKER_URL || ''

export async function* parseSseStream(response, signal) {
  if (!response || !response.body) {
    return
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  try {
    while (true) {
      if (signal && signal.aborted) {
        return
      }

      const { value, done } = await reader.read()
      if (done) {
        break
      }

      buffer += decoder.decode(value, { stream: true })

      let nlIdx
      while ((nlIdx = buffer.indexOf('\n\n')) !== -1) {
        const frame = buffer.slice(0, nlIdx)
        buffer = buffer.slice(nlIdx + 2)

        const lines = frame.split('\n')
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const payload = line.slice(6)
            try {
              yield JSON.parse(payload)
            } catch (e) {
              // skip malformed frames
            }
          }
        }
      }
    }
  } finally {
    try {
      // Close the underlying HTTP stream on early exit; no-op if already done.
      await reader.cancel()
    } catch (e) { /* ignore */ }

    try {
      reader.releaseLock()
    } catch (e) { /* ignore */ }
  }
}

// Fetch with the Cognito bearer attached, retrying once after a token refresh
// on 401 — the same pattern postChat uses, shared by the link/status calls.
async function authedFetch(url, { accessToken, refreshAccessToken, init }) {
  const withAuth = (token) => fetch(url, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(init && init.headers)
    }
  })

  let res = await withAuth(accessToken)
  if (res.status === 401 && typeof refreshAccessToken === 'function') {
    const fresh = await refreshAccessToken()
    if (fresh) {
      res = await withAuth(fresh)
    }
  }

  return res
}

// Link a Ringba account: POST <worker>/admin-link { accountId, apiToken }.
// The browser equivalent of Slack's /ringba-link. Returns { ok, error? }.
export async function linkAccount(options = {}) {
  const { accountId, apiToken, accessToken, refreshAccessToken, workerUrl = DEFAULT_WORKER_URL } = options

  if (!workerUrl) {
    return { ok: false, error: 'Worker URL is not configured.' }
  }

  if (!accessToken) {
    return { ok: false, error: 'Not signed in. Reload and log back in.' }
  }

  const url = `${workerUrl.replace(/\/$/, '')}/admin-link`
  let res
  try {
    res = await authedFetch(url, {
      accessToken,
      refreshAccessToken,
      init: { method: 'POST', body: JSON.stringify({ accountId, apiToken }) }
    })
  } catch (err) {
    return { ok: false, error: `Network error: ${err.message}` }
  }

  if (!res.ok) {
    let detail = res.statusText
    try {
      const body = await res.json()
      detail = body.error || detail
    } catch (e) { /* keep statusText */ }

    return { ok: false, error: detail }
  }

  return { ok: true }
}

// Ask the worker whether an account already has a token (Slack-linked or
// script-seeded counts). Best-effort: on any failure returns ok=false so the
// caller keeps its local guess rather than wrongly flipping the link gate.
export async function checkLinked(options = {}) {
  const { accountId, accessToken, refreshAccessToken, workerUrl = DEFAULT_WORKER_URL } = options

  if (!workerUrl || !accessToken || !accountId) {
    return { linked: false, ok: false }
  }

  const url = `${workerUrl.replace(/\/$/, '')}/admin-link?accountId=${encodeURIComponent(accountId)}`
  let res
  try {
    res = await authedFetch(url, { accessToken, refreshAccessToken, init: { method: 'GET' } })
  } catch (err) {
    return { linked: false, ok: false }
  }

  if (!res.ok) {
    return { linked: false, ok: false }
  }

  try {
    const body = await res.json()
    return { linked: !!body.linked, ok: true }
  } catch (e) {
    return { linked: false, ok: false }
  }
}

function postChat({ workerUrl, accessToken, prompt, accountId, threadHistory, signal }) {
  return fetch(`${workerUrl.replace(/\/$/, '')}/admin-chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`
    },
    body: JSON.stringify({
      prompt,
      accountId,
      threadHistory: threadHistory || []
    }),
    signal
  })
}

function translateEvent(event) {
  switch (event.type) {
    case 'text_chunk':
      return event.phase === 'plan'
        ? { type: 'plan_chunk', text: event.text }
        : { type: 'answer_chunk', text: event.text }
    case 'tool_start':
    case 'tool_result':
    case 'citations':
    case 'error':
    case 'done':
      return event
    default:
      return null
  }
}

export async function runPrompt(prompt, options = {}) {
  const {
    onEvent,
    signal,
    accountId,
    threadHistory,
    accessToken,
    refreshAccessToken,
    workerUrl = DEFAULT_WORKER_URL
  } = options

  const emit = onEvent || (() => {})

  if (!workerUrl) {
    emit({ type: 'error', message: 'Worker URL is not configured.' })
    emit({ type: 'done' })
    return
  }

  if (!accessToken) {
    emit({ type: 'error', message: 'Not signed in. Reload and log back in.' })
    emit({ type: 'done' })
    return
  }

  if (!accountId) {
    emit({
      type: 'error',
      message: 'No Ringba account selected. Link an account first.'
    })
    emit({ type: 'done' })
    return
  }

  let response
  try {
    response = await postChat({ workerUrl, accessToken, prompt, accountId, threadHistory, signal })

    // Token may have expired. Refresh once and retry.
    if (response.status === 401 && typeof refreshAccessToken === 'function') {
      let fresh
      try {
        fresh = await refreshAccessToken()
      } catch (e) {
        emit({ type: 'error', message: 'Session expired. Reload and log back in.' })
        emit({ type: 'done' })
        return
      }

      if (!fresh) {
        emit({ type: 'error', message: 'Session expired. Reload and log back in.' })
        emit({ type: 'done' })
        return
      }

      response = await postChat({ workerUrl, accessToken: fresh, prompt, accountId, threadHistory, signal })
    }
  } catch (err) {
    if (err.name === 'AbortError') {
      emit({ type: 'done' })
      return
    }

    emit({ type: 'error', message: `Network error: ${err.message}` })
    emit({ type: 'done' })
    return
  }

  if (!response.ok) {
    let detail = ''
    try {
      const body = await response.json()
      detail = body.error || JSON.stringify(body)
    } catch (e) {
      detail = response.statusText
    }

    emit({ type: 'error', message: `Worker returned ${response.status}: ${detail}` })
    emit({ type: 'done' })
    return
  }

  if (!response.body) {
    emit({ type: 'error', message: 'Worker returned no stream body.' })
    emit({ type: 'done' })
    return
  }

  try {
    for await (const event of parseSseStream(response, signal)) {
      const translated = translateEvent(event)
      if (!translated) {
        continue
      }

      emit(translated)
      if (translated.type === 'done') {
        return
      }
    }

    emit({ type: 'done' })
  } catch (err) {
    if (err.name === 'AbortError') {
      emit({ type: 'done' })
      return
    }

    emit({ type: 'error', message: `Stream error: ${err.message}` })
    emit({ type: 'done' })
  }
}
